import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWAInstallContextType {
  isInstallable: boolean;
  isInstalled: boolean;
  isIOS: boolean;
  canInstall: boolean;
  install: () => Promise<boolean>;
  showIOSInstructions: boolean;
  setShowIOSInstructions: (show: boolean) => void;
  dismissBanner: () => void;
  shouldShowBanner: boolean;
  hasEngaged: boolean;
}

const PWAInstallContext = createContext<PWAInstallContextType | null>(null);

const BANNER_DISMISS_KEY = 'pwa-banner-dismissed';
const BANNER_DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

export function PWAInstallProvider({ children }: { children: ReactNode }) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [hasEngaged, setHasEngaged] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    const checkInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSStandalone = (navigator as any).standalone === true;
      setIsInstalled(isStandalone || isIOSStandalone);
    };

    // Check if iOS
    const checkIOS = () => {
      const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
      setIsIOS(isIOSDevice);
      // On iOS, we can "install" via Add to Home Screen
      if (isIOSDevice && !isInstalled) {
        setIsInstallable(true);
      }
    };

    // Check if banner was dismissed recently
    const checkBannerDismissed = () => {
      const dismissedAt = localStorage.getItem(BANNER_DISMISS_KEY);
      if (dismissedAt) {
        const dismissTime = parseInt(dismissedAt, 10);
        if (Date.now() - dismissTime < BANNER_DISMISS_DURATION) {
          setBannerDismissed(true);
        } else {
          localStorage.removeItem(BANNER_DISMISS_KEY);
        }
      }
    };

    checkInstalled();
    checkIOS();
    checkBannerDismissed();

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    // Track user engagement (scroll, click)
    const handleEngagement = () => {
      setHasEngaged(true);
      window.removeEventListener('scroll', handleEngagement);
      window.removeEventListener('click', handleEngagement);
    };

    // Delay engagement tracking
    const engagementTimeout = setTimeout(() => {
      window.addEventListener('scroll', handleEngagement, { once: true });
      window.addEventListener('click', handleEngagement, { once: true });
    }, 10000); // Wait 10 seconds before starting to track

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      clearTimeout(engagementTimeout);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('scroll', handleEngagement);
      window.removeEventListener('click', handleEngagement);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- isInstalled intentionally excluded; adding it restarts the 10s engagement timer on every install state change

  const install = useCallback(async () => {
    // For iOS, show instructions modal
    if (isIOS) {
      setShowIOSInstructions(true);
      return false;
    }

    if (!deferredPrompt) {
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
      }

      setDeferredPrompt(null);
      return outcome === 'accepted';
    } catch (error) {
      console.error('Error installing PWA:', error);
      return false;
    }
  }, [deferredPrompt, isIOS]);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    localStorage.setItem(BANNER_DISMISS_KEY, Date.now().toString());
  }, []);

  const shouldShowBanner = isInstallable && !isInstalled && !bannerDismissed && hasEngaged;

  return (
    <PWAInstallContext.Provider
      value={{
        isInstallable,
        isInstalled,
        isIOS,
        canInstall: isInstallable && !isInstalled,
        install,
        showIOSInstructions,
        setShowIOSInstructions,
        dismissBanner,
        shouldShowBanner,
        hasEngaged,
      }}
    >
      {children}
    </PWAInstallContext.Provider>
  );
}

export const usePWAInstall = () => {
  const context = useContext(PWAInstallContext);
  if (!context) {
    throw new Error('usePWAInstall must be used within PWAInstallProvider');
  }
  return context;
};